// utils/formatters.ts

// Currency conversion rates (update these periodically or fetch from API)
const CURRENCY_RATES: Record<string, number> = {
  USD: 1,
  EUR: 0.85,
  GBP: 0.73,
  JPY: 110.5,
};

export let globalCurrency = localStorage.getItem("preferred_currency") || "USD";
export let xrpPriceInUSD = parseFloat(
  localStorage.getItem("xrp_price_usd") || "2.13",
);

export const setGlobalCurrency = (currency: string) => {
  globalCurrency = currency;
  localStorage.setItem("preferred_currency", currency);
};

export const setXrpPriceInUSD = (price: number) => {
  xrpPriceInUSD = price;
  localStorage.setItem("xrp_price_usd", price.toString());
  localStorage.setItem("xrp_price_updated", Date.now().toString());
};

export const getXrpPrice = (currency = globalCurrency): number => {
  const rate = CURRENCY_RATES[currency] || 1;
  return xrpPriceInUSD * rate;
};

export const getCurrencyRate = (currency: string): number => {
  return CURRENCY_RATES[currency] || 1;
};

export const formatCurrency = (
  amount: number,
  currency = globalCurrency,
): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatNumber = (num: number, decimals = 2): string => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

export const formatDate = (date: Date | string, includeTime = true): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (includeTime) {
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return dateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const formatRelativeTime = (date: Date | string): string => {
  const now = new Date();
  const past = typeof date === "string" ? new Date(date) : date;
  const diffMs = now.getTime() - past.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return formatDate(past, false);
};

export const truncateAddress = (
  address: string,
  start = 6,
  end = 4,
): string => {
  if (address.length <= start + end) return address;
  return `${address.slice(0, start)}...${address.slice(-end)}`;
};

export const validateXrpAddress = (address: string): boolean => {
  if (!address.startsWith("r") || address.length !== 34) return false;
  return /^r[1-9A-HJ-NP-Za-km-z]{25,34}$/.test(address);
};

// New function to get transaction type icon
export const getTransactionTypeIcon = (type: string) => {
  switch (type) {
    case "deposit":
      return "arrow-up";
    case "withdrawal":
      return "arrow-down";
    case "trade_earning":
      return "currency-dollar";
    case "airdrop":
      return "gift";
    case "bot_earning":
      return "rocket-launch";
    case "referral_earning":
      return "user-group";
    default:
      return "banknotes";
  }
};

// New function to get transaction type label
export const getTransactionTypeLabel = (type: string) => {
  switch (type) {
    case "deposit":
      return "Deposit";
    case "withdrawal":
      return "Withdrawal";
    case "trade_earning":
      return "Trading Earnings";
    case "airdrop":
      return "Airdrop";
    case "bot_earning":
      return "Bot Earnings";
    case "referral_earning":
      return "Referral Earnings";
    default:
      return type.replace("_", " ");
  }
};

// New function to get transaction type color
export const getTransactionTypeColor = (type: string) => {
  switch (type) {
    case "deposit":
      return "text-green-500";
    case "withdrawal":
      return "text-red-500";
    case "trade_earning":
    case "bot_earning":
    case "referral_earning":
      return "text-blue-500";
    case "airdrop":
      return "text-purple-500";
    default:
      return "text-gray-500";
  }
};

// New function to get all transaction types
export const getTransactionTypes = () => {
  return [
    "deposit",
    "withdrawal",
    "trade_earning",
    "airdrop",
    "bot_earning",
    "referral_earning",
  ];
};

/**
 * Formats XRP amounts with exactly 2 decimal places
 * @param amount - XRP amount (drops or decimal)
 * @param fromDrops - Whether the input is in drops (1 XRP = 1,000,000 drops)
 * @returns Formatted XRP amount as string with 2 decimal places
 */
export const formatXRP = (
  amount: number | string,
  fromDrops = false,
): string => {
  let xrpAmount: number;

  if (fromDrops) {
    // Convert from drops to XRP (1 XRP = 1,000,000 drops)
    const drops = typeof amount === "string" ? parseFloat(amount) : amount;
    xrpAmount = drops / 1000000;
  } else {
    xrpAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  }

  // Format with exactly 2 decimal places
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(xrpAmount);
};
